import * as turf from '@turf/turf';
import { GeoJSONSource, LngLat, Map as MaplibreMap, MapMouseEvent } from 'maplibre-gl';
import { v4 } from 'uuid';
import assert from '../../../utils/assert';
import { DrawingObjectCenterUnits, DrawingObjectCircleGeometry, DrawingObjectOffsetUnits } from '../types';
import { FeDrawMode } from './draw-mode';

const STEPS = 100;

export class CircleEditMode extends FeDrawMode {
    constructor(
        private readonly map: MaplibreMap,
        private readonly source: GeoJSONSource
    ) {
        super();
    }

    private lastDragPoint: LngLat | null = null;
    private dragging = false;
    private resizing = false;
    private currentRadius = [0, 0];
    private centerPoint: number[] | null = null;
    private feUnits: DrawingObjectOffsetUnits = DrawingObjectOffsetUnits.degrees;
    private turfUnits: turf.Units = 'nauticalmiles';
    private featureId: string | null = null;
    private points: GeoJSON.Feature<GeoJSON.Point>[] = [];
    private centerPointFeature: GeoJSON.Feature<GeoJSON.Point> | null = null;

    override cancel(): void {
        this.source.setData(turf.featureCollection([]));
    }

    override onMouseDown = (e: MapMouseEvent) => {
        const features = this.map.queryRenderedFeatures(e.point, { layers: ['fe-drawing-points-outline', 'fe-drawing-fill'] });

        if (features.length === 0) {
            return;
        }

        e.preventDefault();
        this.resizing = features.some((x) => x.layer.id === 'fe-drawing-points-outline' && !x.properties?.center);
        this.lastDragPoint = e.lngLat;
        this.dragging = true;
    };

    override onMouseMove(e: MapMouseEvent): void {
        if (this.dragging) {
            assert(this.centerPoint);
            if (this.resizing) {
                assert(this.lastDragPoint);
                if (e.originalEvent.shiftKey) {
                    this.currentRadius[0] = this.currentRadius[1] = turf.distance(this.centerPoint, e.lngLat.toArray(), {
                        units: this.turfUnits
                    });
                } else {
                    this.currentRadius[0] = turf.distance(this.centerPoint, [e.lngLat.lng, this.centerPoint[1]], { units: this.turfUnits });
                    this.currentRadius[1] = turf.distance(this.centerPoint, [this.centerPoint[0], e.lngLat.lat], { units: this.turfUnits });
                }
                this.updateGeometry();
            } else {
                assert(this.lastDragPoint);
                const deltaLng = e.lngLat.lng - this.lastDragPoint.lng;
                const deltaLat = e.lngLat.lat - this.lastDragPoint.lat;
                this.lastDragPoint = e.lngLat;

                this.centerPoint[0] += deltaLng;
                this.centerPoint[1] += deltaLat;
                this.updateGeometry();
            }
        }
    }

    override onMouseUp = (e: MapMouseEvent) => {
        if (this.dragging) {
            e.preventDefault();
            if (!this.resizing) {
                assert(this.lastDragPoint);
                assert(this.centerPoint);
                this.centerPoint[0] += e.lngLat.lng - this.lastDragPoint.lng;
                this.centerPoint[1] += e.lngLat.lat - this.lastDragPoint.lat;
            }
            this.dragging = false;
            this.resizing = false;
        }
    };

    override edit(geometry: DrawingObjectCircleGeometry) {
        assert(geometry.centerUnits === DrawingObjectCenterUnits.latLng);

        this.dragging = false;
        this.resizing = false;
        this.centerPoint = [geometry.centerX, geometry.centerY];
        this.currentRadius = [geometry.radiusX, geometry.radiusY];
        this.feUnits = geometry.radiusUnits;
        this.turfUnits = feUnitsToTurf[this.feUnits];
        this.featureId = v4();
        this.points = [];
        for (let i = 0; i < STEPS / 8; i++) {
            this.points.push({
                id: i,
                type: 'Feature',
                geometry: {
                    type: 'Point',
                    coordinates: this.centerPoint
                },
                properties: {}
            });
        }
        this.centerPointFeature = turf.point(this.centerPoint, { center: true, active: true }, { id: v4() });

        this.source.setData({
            type: 'FeatureCollection',
            features: [
                {
                    type: 'Feature',
                    id: this.featureId,
                    geometry: {
                        type: 'Polygon',
                        coordinates: [[this.centerPoint, this.centerPoint]]
                    },
                    properties: {}
                },
                this.centerPointFeature,
                ...this.points
            ]
        });
        this.updateGeometry();
    }

    override getEdited(): DrawingObjectCircleGeometry {
        assert(this.centerPoint);
        return {
            centerX: this.centerPoint[0],
            centerY: this.centerPoint[1],
            centerUnits: DrawingObjectCenterUnits.latLng,
            radiusX: this.currentRadius[0],
            radiusY: this.currentRadius[1],
            radiusUnits: this.feUnits
        };
    }

    private updateGeometry() {
        assert(this.centerPointFeature);
        if (this.currentRadius[0] === 0 || this.currentRadius[1] === 0) {
            return;
        }

        const newGeometry = turf.ellipse(this.centerPoint!, this.currentRadius[0], this.currentRadius[1], {
            units: this.turfUnits,
            steps: STEPS
        }).geometry;
        for (let i = 0; i < this.points.length; i++) {
            this.points[i].geometry.coordinates = newGeometry.coordinates[0][i * 8];
        }
        this.centerPointFeature.geometry.coordinates = this.centerPoint!;
        this.source.updateData({
            update: [
                {
                    id: this.featureId!,
                    newGeometry: newGeometry
                },
                {
                    id: this.centerPointFeature.id as string,
                    newGeometry: this.centerPointFeature.geometry
                },
                ...this.points.map((x, i) => ({
                    id: x.id as number,
                    newGeometry: x.geometry
                }))
            ]
        });
    }
}

const feUnitsToTurf: Record<DrawingObjectOffsetUnits, turf.Units> = {
    [DrawingObjectOffsetUnits.miles]: 'nauticalmiles',
    [DrawingObjectOffsetUnits.degrees]: 'degrees'
} as const;
